<template>
  <div class="petpage-wrapper">
    <div class="bg-orb bg-orb-1"></div>
    <div class="bg-orb bg-orb-2"></div>

    <!-- Loading -->
    <div v-if="loading" class="petpage-center">
      <div class="loading-card glass-strong">
        <v-icon dark size="40" color="rgba(255,255,255,0.4)" class="spin">mdi-loading</v-icon>
        <p>Buscando informações do pet...</p>
      </div>
    </div>

    <!-- Erro / não encontrado -->
    <div v-else-if="error" class="petpage-center">
      <div class="error-card glass-strong">
        <v-icon dark size="52" color="rgba(231,76,60,0.8)">mdi-paw-off</v-icon>
        <h3>Pet não encontrado</h3>
        <p>Nenhum pet cadastrado com o código <strong>{{ codigo }}</strong>.</p>
        <router-link to="/" class="btn-primary back-home-btn">
          <v-icon dark size="16">mdi-arrow-left</v-icon>
          Tentar outro código
        </router-link>
      </div>
    </div>

    <!-- Pet encontrado -->
    <div v-else class="petpage-center">
      <div class="pet-card glass-strong">

        <!-- Topo: foto + status -->
        <div class="pet-top">
          <div class="pet-photo-wrap" :class="{ 'photo-missing': pet.isDesaparecido }">
            <img
              :src="pet.B64Foto || require('@/assets/logoCard.png')"
              class="pet-photo"
              @error="onImgError"
            />
          </div>

          <div
            class="pet-status-badge"
            :class="pet.isDesaparecido ? 'badge-missing' : 'badge-safe'"
          >
            <v-icon dark size="13">
              {{ pet.isDesaparecido ? 'mdi-alert-circle' : 'mdi-check-decagram' }}
            </v-icon>
            {{ pet.isDesaparecido ? 'DESAPARECIDO' : 'PROTEGIDO' }}
          </div>
        </div>

        <!-- Nome -->
        <div class="pet-name-section">
          <h1 class="pet-name">{{ pet.Nome }}</h1>
          <p class="pet-owner" v-if="pet.Portador">
            <v-icon dark size="14">mdi-account-outline</v-icon>
            Tutor: {{ pet.Portador }}
          </p>
        </div>

        <!-- Grid de info -->
        <div class="info-grid">
          <div class="info-block glass">
            <v-icon dark size="20" class="info-icon">mdi-paw</v-icon>
            <span class="info-label">Raça</span>
            <span class="info-val">{{ pet.Raca || '—' }}</span>
          </div>
          <div class="info-block glass">
            <v-icon dark size="20" class="info-icon">mdi-cake-variant-outline</v-icon>
            <span class="info-label">Idade</span>
            <span class="info-val">{{ pet.Idade ? pet.Idade + ' anos' : '—' }}</span>
          </div>
          <div class="info-block glass">
            <v-icon dark size="20" class="info-icon">mdi-map-marker-outline</v-icon>
            <span class="info-label">Localização</span>
            <span class="info-val truncate">{{ pet.Endereco || '—' }}</span>
          </div>
          <div class="info-block glass">
            <v-icon dark size="20" class="info-icon">mdi-shield-check-outline</v-icon>
            <span class="info-label">Microchip</span>
            <span class="info-val">{{ pet.IdMicrochip || '—' }}</span>
          </div>
        </div>

        <!-- Alerta de desaparecido -->
        <div v-if="pet.isDesaparecido" class="missing-alert">
          <v-icon dark size="18" color="#e74c3c">mdi-alert</v-icon>
          <p>
            Este pet está desaparecido. Se você o encontrou, por favor entre em contato com o tutor imediatamente.
          </p>
        </div>

        <!-- Descrição -->
        <div v-if="pet.Descricao" class="bio-block glass">
          <v-icon dark size="16" class="bio-quote">mdi-format-quote-open</v-icon>
          <p class="bio-text">{{ pet.Descricao }}</p>
        </div>

        <!-- Ação -->
        <a
          href="https://wa.me/553799512453"
          target="_blank"
          rel="noopener noreferrer"
          class="btn-whatsapp contact-btn"
        >
          <svg class="wa-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          CONTATAR TUTOR VIA WHATSAPP
        </a>

        <router-link to="/" class="home-link">
          <v-icon dark size="13">mdi-arrow-left</v-icon>
          Buscar outro pet
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "PetPage",

  data() {
    return {
      codigo: null,
      loading: true,
      error: false,
      pet: {},
    };
  },

  mounted() {
    this.codigo = this.$route.params.codigo;
    this.buscarPet();
  },

  methods: {
    async buscarPet() {
      this.loading = true;
      this.error = false;
      try {
        const { data } = await axios.get(`${this.$api}/api/consultar/${this.codigo}`);
        this.pet = data;

        if (data.isDesaparecido) {
          this.registrarVisita(data);
        }
      } catch {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    registrarVisita(pet) {
      const payload = {
        PetNome: pet.Nome,
        UserAgent: navigator.userAgent,
      };

      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            payload.Latitude = String(pos.coords.latitude);
            payload.Longitude = String(pos.coords.longitude);
            this.enviarLog(pet.Id, payload);
          },
          () => {
            // Permissão negada — envia sem coordenadas
            this.enviarLog(pet.Id, payload);
          },
          { timeout: 6000, maximumAge: 0 }
        );
      } else {
        this.enviarLog(pet.Id, payload);
      }
    },

    enviarLog(petId, payload) {
      axios
        .post(`${this.$api}/api/log-visita/${petId}`, payload)
        .catch(() => {}); // silencioso — não atrapalha o usuário
    },

    onImgError(e) {
      e.target.src = require("@/assets/logoCard.png");
    },

  },
};
</script>

<style scoped>
.petpage-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 24px;
}

.petpage-center {
  width: 100%;
  max-width: 460px;
  position: relative;
  z-index: 1;
}

/* Loading / Error cards */
.loading-card,
.error-card {
  border-radius: 28px;
  padding: 56px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.loading-card p,
.error-card p {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.88rem;
  margin: 0;
}

.error-card h3 {
  color: #fff;
  font-size: 1.2rem;
  margin: 0;
}

.error-card strong {
  color: var(--primary-light, #5fd1cb);
}

.back-home-btn {
  text-decoration: none;
  border-radius: 12px;
  padding: 12px 22px;
  font-size: 0.85rem;
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* Pet card */
.pet-card {
  border-radius: 28px;
  padding: 36px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

/* Photo top */
.pet-top {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.pet-photo-wrap {
  width: 130px;
  height: 130px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: border-color 0.3s;
}

.pet-photo-wrap.photo-missing {
  border-color: rgba(231, 76, 60, 0.6);
  box-shadow: 0 0 0 4px rgba(231, 76, 60, 0.15), 0 8px 32px rgba(0, 0, 0, 0.3);
}

.pet-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pet-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 14px;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1px;
}

.badge-safe {
  background: rgba(39, 174, 96, 0.2);
  border: 1px solid rgba(39, 174, 96, 0.4);
  color: #2ecc71;
}

.badge-missing {
  background: rgba(231, 76, 60, 0.2);
  border: 1px solid rgba(231, 76, 60, 0.4);
  color: #e74c3c;
}

/* Name */
.pet-name-section {
  text-align: center;
}

.pet-name {
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.pet-owner {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.82rem;
  margin: 8px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

/* Info grid */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
}

.info-block {
  border-radius: 14px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-icon {
  color: var(--primary-light, #5fd1cb) !important;
  margin-bottom: 4px;
}

.info-label {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.info-val {
  font-size: 0.9rem;
  color: #fff;
  font-weight: 600;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}

/* Missing alert */
.missing-alert {
  width: 100%;
  background: rgba(231, 76, 60, 0.12);
  border: 1px solid rgba(231, 76, 60, 0.3);
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.missing-alert p {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.82rem;
  line-height: 1.5;
  margin: 0;
}

/* Bio */
.bio-block {
  width: 100%;
  border-radius: 14px;
  padding: 16px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.bio-quote {
  color: var(--primary-light, #5fd1cb) !important;
  flex-shrink: 0;
  margin-top: 2px;
}

.bio-text {
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.85rem;
  line-height: 1.6;
  margin: 0;
  font-style: italic;
}

/* WhatsApp button */
.btn-whatsapp {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #25d366;
  border: none;
  border-radius: 14px;
  color: #fff;
  font-family: "Comfortaa", sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 1px;
  padding: 15px 24px;
  text-decoration: none;
  transition: all 0.25s ease;
  box-shadow: 0 6px 24px rgba(37, 211, 102, 0.3);
}

.btn-whatsapp:hover {
  background: #1ebe5d;
  transform: translateY(-2px);
  box-shadow: 0 10px 32px rgba(37, 211, 102, 0.45);
}

.wa-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.home-link {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.78rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.2s;
}

.home-link:hover {
  color: rgba(255, 255, 255, 0.8);
}

/* Spin */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
